import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type SubscriptionTier = "free" | "starter" | "pro";

const TIER_LIMITS = {
  free: { maxProjects: 1, maxDailyBuilds: 3, monthlyEditsLimit: 0, customDomains: false, freeBuildsLimit: 1 },
  starter: { maxProjects: 3, maxDailyBuilds: 50, monthlyEditsLimit: 30, customDomains: true, freeBuildsLimit: Infinity },
  pro: { maxProjects: Infinity, maxDailyBuilds: 999, monthlyEditsLimit: 150, customDomains: true, freeBuildsLimit: Infinity },
} as const;

function buildCountKey(userId: string): string {
  const date = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  return `dayone:buildCount:${userId}:${date}`;
}

export function useSubscription() {
  const { user } = useAuth();
  const [tier, setTier] = useState<SubscriptionTier>("free");
  const [stripeCustomerId, setStripeCustomerId] = useState<string | null>(null);
  const [stripeSubscriptionId, setStripeSubscriptionId] = useState<string | null>(null);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("subscription_tier, stripe_customer_id, stripe_subscription_id, current_period_end")
        .eq("id", user.id)
        .single();

      if (error) {
        console.warn("useSubscription: could not fetch profile", error.message);
      } else if (data) {
        setTier((data.subscription_tier as SubscriptionTier) || "free");
        setStripeCustomerId(data.stripe_customer_id || null);
        setStripeSubscriptionId(data.stripe_subscription_id || null);
        setCurrentPeriodEnd(data.current_period_end || null);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const limits = useMemo(() => TIER_LIMITS[tier], [tier]);
  const isProUser = tier === "pro";
  const isStarterUser = tier === "starter";
  const isPaidUser = tier === "starter" || tier === "pro";
  const isFreeUser = tier === "free";

  // TODO: re-enable project limits before public launch
  const canCreateProject = useCallback(
    (_currentCount: number) => true,
    [],
  );

  // TODO: re-enable build limits before public launch
  const canBuild = useCallback(
    (_dailyCount: number) => true,
    [],
  );

  const canUseCustomDomain = limits.customDomains;

  const getDailyBuildCount = useCallback((): number => {
    if (!user?.id) return 0;
    try {
      const val = localStorage.getItem(buildCountKey(user.id));
      return val ? parseInt(val, 10) || 0 : 0;
    } catch {
      return 0;
    }
  }, [user?.id]);

  const incrementBuildCount = useCallback(() => {
    if (!user?.id) return;
    try {
      const key = buildCountKey(user.id);
      const current = parseInt(localStorage.getItem(key) || "0", 10) || 0;
      localStorage.setItem(key, String(current + 1));
    } catch {
      // ignore
    }
  }, [user?.id]);

  return {
    tier,
    loading,
    stripeCustomerId,
    stripeSubscriptionId,
    currentPeriodEnd,
    isProUser,
    isStarterUser,
    isPaidUser,
    isFreeUser,
    limits,
    canCreateProject,
    canBuild,
    canUseCustomDomain,
    getDailyBuildCount,
    incrementBuildCount,
    refetch: fetchSubscription,
  };
}
